import type { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';

import type { ButtonProps } from '@/components/Buttons/Button';
import Button from '@/components/Buttons/Button';
import { spacing } from '@/constants/ui';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  buttons?: ReactNode;
  closeIcon?: boolean;
};

export const Dialog = ({ open, onClose, title, children, buttons, closeIcon }: DialogProps) => (
  <MuiDialog
    open={open}
    slotProps={{
      paper: {
        sx: {
          backgroundColor: 'background.default'
        }
      },
      backdrop: {
        sx: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(18, 22, 63, 0.80)'
        }
      }
    }}
    sx={{ zIndex: 999999999 }}
    onClose={onClose}
  >
    <DialogContent
      sx={{
        minWidth: 590,
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {(title || closeIcon) && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: spacing[1.5]
          }}
        >
          {title && <Typography variant='subtitle2'>{title}</Typography>}

          {closeIcon && (
            <IconButton
              sx={{ p: 0 }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}
      {children}
      {buttons && <DialogActions sx={{ mt: spacing[2] }}>{buttons}</DialogActions>}
    </DialogContent>
  </MuiDialog>
);

export const DialogButton = ({
  onClick,
  children,
  variant,
  ...rest
}: Pick<ButtonProps, 'variant' | 'children' | 'onClick' | 'disabled'>) => (
  <Button
    size='small'
    sx={{
      minWidth: '120px'
    }}
    variant={variant}
    onClick={onClick}
    {...rest}
  >
    {children}
  </Button>
);

export const DialogContentText = ({ children }: { children: ReactNode }) => (
  <Typography variant='body1'>{children}</Typography>
);
